import {
  bodyMixin,
  OwnUpBody,
  OwnUpComponentLibraryProvider,
  OwnUpFillButtonPrimary,
  OwnUpGridContainer,
  OwnUpGridItem,
  OwnUpGridWrapper,
  OwnUpHeadlineBook,
  OwnUpSmallSubheadlineRegular,
  OwnUpSpinner,
  OwnUpSubheadlineMedium,
  OwnUpTextInput
} from '@rategravity/own-up-component-library';
import * as colors from '@rategravity/own-up-component-library/colors';
import { useInternalAuth } from '@rategravity/widgets-react/modules/auth/internal/hooks/use-internal-auth';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { OwnUpTextArea } from '../../components/textarea';
import { Response, useAdjustLeadPrice } from './use-adjust-lead-price';

const ResultDisplay = ({ status, response }: Response) => {
  switch (status) {
    case 'Sending':
      return <OwnUpSpinner />;
    case 'Success':
    case 'Failed':
      return (
        <div>
          <h2>{status}</h2>
          <h3>Response</h3>
          <pre>{JSON.stringify(response, undefined, 2)}</pre>
        </div>
      );
    default:
      return null;
  }
};

const FileInput = styled.input`
  color: ${colors.WHITE_100};
  background-color: ${colors.ALOE_100};
  border-radius: 8px;
  padding: 8px 16px;
  ${bodyMixin};
  :hover {
    background-color: ${colors.PINE_100};
    cursor: pointer;
  }
`;

/**
 * Page for one-time retroactive lead price adjustments.
 */
export const PriceAdjustmentsPage = () => {
  const { isLoggedIn, userGroups, login } = useInternalAuth();
  const [data, setData] = useState('');
  const [rationale, setRationale] = useState('');
  const [result, send] = useAdjustLeadPrice();
  const onAdjustPrices = useCallback(() => {
    send({ data, rationale });
  }, [data, rationale, send]);
  useEffect(() => {
    if (!isLoggedIn) {
      void login(window.location.href);
    }
  }, [isLoggedIn, login]);

  // Only allow access to users in the 'dev' or 'lender-management' groups
  const canAccess = useMemo(
    () => userGroups.some((group) => group === 'dev' || group === 'lender-management'),
    [userGroups]
  );

  if (!isLoggedIn) {
    return <span>Redirecting to login.</span>;
  }
  if (!canAccess) {
    return <span>You are not authorized to access this page.</span>;
  }
  return (
    <OwnUpComponentLibraryProvider>
      <OwnUpGridWrapper>
        <OwnUpGridContainer variant="slim">
          <OwnUpGridItem xs={12}>
            <OwnUpHeadlineBook variant="h1" style={{ textAlign: 'center' }}>
              Retroactive Lead Pricing Adjustments
            </OwnUpHeadlineBook>
            <OwnUpSubheadlineMedium variant="h2" style={{ marginTop: '16px' }}>
              Background
            </OwnUpSubheadlineMedium>
            <OwnUpBody variant="body1">
              This tool is for one-time retroactive price adjstments. For each lead entered, we will
              send an event and adjust the budget (as long as this price adjustment is in the same
              month the lead was originally sent).
              <OwnUpSubheadlineMedium variant="h2" style={{ marginTop: '16px' }}>
                Directions
              </OwnUpSubheadlineMedium>
              In order to use this tool you must fill out all the fields. You&apos;ll need to
              compile a list of all the leads you want to adjust price for. Data should be a
              collection of &quot;leadID, lenderId, finalPrice&quot; and Rationale is a short string
              explaining the reason for the adjustment. The data can be in either JSON or CSV
              format:
              <br />
              <OwnUpSmallSubheadlineRegular variant="h3" style={{ marginTop: '16px' }}>
                JSON Example
              </OwnUpSmallSubheadlineRegular>
              <code>
                [&#123; &quot;leadID&quot;: &quot;leadIdA&quot;, &quot;lenderId&quot;:
                &quot;rocketMortgage&quot;, &quot;finalPrice&quot;: 50 &#125;,
                &#123;&quot;leadID&quot;: &quot;leadIdB&quot;, &quot;lenderId&quot;:
                &quot;rocketMortgage&quot;, &quot;finalPrice&quot;: 75 &#125;,
                &#123;&quot;leadID&quot;: &quot;leadIdC&quot;, &quot;lenderId&quot;:
                &quot;omahaMortgage&quot;, &quot;finalPrice&quot;: 50 &#125;]
              </code>
              <OwnUpSmallSubheadlineRegular variant="h3" style={{ marginTop: '16px' }}>
                CSV Example
              </OwnUpSmallSubheadlineRegular>
              <code>
                leadID,lenderId,finalPrice
                <br />
                leadIdA,rocketMortgage,50
                <br />
                leadIdB,rocketMortgage,75
                <br />
                leadIdC,omahaMortgage,50
              </code>
              <br />
              <br />
              NOTE: This could affect the shared leads price adjustment, which automatically runs 24
              hours after the lead was originally sent. Pricing adjustments happen in the order they
              are received. If you want this price adjustment to be the last one applied, you should
              wait more than 24 hours after the lead was originally sent.
            </OwnUpBody>
          </OwnUpGridItem>
          <OwnUpGridItem xs={12} style={{ marginTop: '16px' }}>
            <OwnUpSmallSubheadlineRegular variant="h3" style={{ marginTop: '16px' }}>
              Upload or Paste Data
            </OwnUpSmallSubheadlineRegular>
          </OwnUpGridItem>
          <OwnUpGridItem xs={12} style={{ marginTop: '16px' }}>
            <FileInput
              id="data-file-upload"
              type="file"
              accept=".json,.csv"
              onChange={(event) => {
                void (async () => {
                  const file = event.target.files?.[0];
                  if (!file) {
                    return;
                  }
                  const text = await file.text();
                  setData(text);
                })();
              }}
            />
          </OwnUpGridItem>
          <OwnUpGridItem xs={12} style={{ marginTop: '16px' }}>
            <OwnUpTextArea
              placeholder="Data"
              value={data}
              onChange={(event) => setData(event.target.value)}
            />
          </OwnUpGridItem>
          <OwnUpGridItem xs={12} style={{ marginTop: '16px' }}>
            <OwnUpTextInput
              label="Rationale"
              value={rationale}
              onChange={(event) => setRationale(event.target.value)}
            />
          </OwnUpGridItem>
          <OwnUpGridItem xs={12} style={{ marginTop: '16px' }}>
            <OwnUpFillButtonPrimary onClick={onAdjustPrices}>
              Adjust Lead Prices
            </OwnUpFillButtonPrimary>
          </OwnUpGridItem>
          {result.status === 'Sending' ? (
            <OwnUpGridItem xs={12}>
              <OwnUpSpinner />
            </OwnUpGridItem>
          ) : (
            <OwnUpGridItem
              xs={12}
              style={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              <ResultDisplay {...result} />
            </OwnUpGridItem>
          )}
        </OwnUpGridContainer>
      </OwnUpGridWrapper>
    </OwnUpComponentLibraryProvider>
  );
};
